<template>
    <div
        class="ppv2-flex ppv2-flex-wrap ppv2-w-full ppv2-step-wrapper md:ppv2-gap-[3.125vw]"
        :style="{ paddingLeft: this.normalizeSize(55), paddingRight: this.normalizeSize(55) }"
    >
        <div class="form-section ppv2-py-4">
            <HeaderStep :step="step" :sizes="sizes" />
            <div class="ppv2-flex ppv2-flex-wrap" :style="{ gap: normalizeSize(20, true) }">
                <div
                    v-for="(additional_sale, index) in filteredAdditionalSales"
                    :key="index"
                    class="ppv2-min-w-[120px] ppv2-min-h-[120px]"
                    :style="{
                        width: getCardWidth(),
                        aspectRatio: '1/1',
                        borderRadius: normalizeSize(14),
                    }"
                >
                    <AdditionalSaleCard
                        @update:item="updateAdditionalSaleLocal($event, getOriginalIndex(additional_sale))"
                        @edit="openEditPopup($event, getOriginalIndex(additional_sale))"
                        @delete="deleteItem(getOriginalIndex(additional_sale))"
                        :item="additional_sale"
                        :productPurchaseOptions="localPrestation?.product_purchase_options"
                        :hasAccess="hasAccess"
                        :permission-modify-additional-sales="permissionModifyAdditionalSales"
                        :permission-delete-additional-sales="permissionDeleteAdditionalSales"
                    />
                </div>
                <div :style="{ width: getCardWidth(), aspectRatio: '1/1' }">
                    <div
                        v-if="permissionCreateAdditionalSales"
                        @click="openPopup"
                        class="my-block d-flex flex-column align-center justify-center ppv2-border ppv2-border-solid ppv2-border-black ppv2-text-black"
                    >
                        <Plus />
                    </div>
                </div>
            </div>
            <div class="form-actions" :class="{ '!ppv2-justify-end': this.complited }">
                <BackButton v-show="!this.complited" type="button" @click="$emit('previous-step')" />
                <BaseButton id="submit" type="submit" variant="primary" @click="submitForm" :loading="loading">
                    {{ !this.complited ? $t('experience.next') : $t('common.save') }}
                </BaseButton>
            </div>
        </div>
        <CustomDialog v-model="popupVisible" :defaultTitle="popupTitle" customClassTitle="poppinsSemiBold">
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('experience.descriptionClosePopup') }}
                </p>
                <div class="upload-container">
                    <CustomInput
                        :label="$t('experience.step6.labelNom')"
                        :required="true"
                        v-model="popupItem.name"
                        :placeholder="$t('experience.step6.nom')"
                        :state="getState('name')"
                        :errorText="errors.name"
                        @input="clearError('name')"
                        :customSizeLabel="14"
                        customClassLabel="poppinsMedium ppv2-text-black"
                    />
                    <BaseSelect
                        :label="$t('product.category')"
                        v-model="popupItem.category_selection"
                        :options="categoryOptions"
                        :placeholder="$t('product.category_placeholder')"
                        :required="true"
                        :error="
                            errors.category_id
                                ? Array.isArray(errors.category_id)
                                    ? errors.category_id[0]
                                    : errors.category_id
                                : ''
                        "
                        @input="clearError('category_id')"
                        :customSizeLabel="14"
                        customLabelClass="poppinsMedium ppv2-text-black"
                        fixed-dropdown
                    />
                    <CustomInputUpload
                        :label="$t('experience.step6.labelUpload')"
                        v-model="popupItem.image"
                        :required="false"
                        @input="onFileSelected"
                        :customSizeLabel="14"
                        customClassLabel="poppinsMedium ppv2-text-black"
                    />
                    <BaseTextarea
                        :label="$t('experience.step6.labelDescription')"
                        v-model="popupItem.description"
                        :required="false"
                        :placeholder="$t('experience.step6.description')"
                        :state="getState('description')"
                        :useEditor="false"
                        :errorText="errors.description"
                        @input="clearError('description')"
                        :rows="3"
                        :customLabelStyle="`font-size: ${normalizeSize(14, true)}`"
                        customLabelClass="poppinsMedium ppv2-text-black"
                    />
                    <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2" :style="{ gap: normalizeSize(35, true) }">
                        <CustomInput
                            :label="$t('experience.step6.labelPrice')"
                            :right-icon="$symbolCurrency"
                            type="number"
                            :min="0"
                            :required="true"
                            v-model="popupItem.price"
                            placeholder="Ex: 10"
                            :state="getState('price')"
                            :errorText="errors.price"
                            @input="clearError('price')"
                            @focus="activeField = 'price'"
                            @blur="activeField = null"
                            :customSizeLabel="14"
                            customClassLabel="poppinsMedium ppv2-text-black"
                        />
                        <BaseSelect
                            class="ppv2-min-w-60"
                            :label="$t('experience.step6.labelTva')"
                            :required="true"
                            v-model="popupItem.tva"
                            :options="option_tva"
                            :error="errors.tva"
                            option-label="label"
                            @input="clearError('tva')"
                            option-value="value"
                            :placeholder="$t('common.placeholderSelect')"
                            :customSizeLabel="14"
                            customLabelClass="poppinsMedium ppv2-text-black"
                            fixed-dropdown
                        />
                    </div>

                    <div class="pb-5">
                        <BaseSwitch
                            v-model="popupItem.participant_based_quantity"
                            color="primary"
                            :label="$t('experience.step6.switch1')"
                        />
                    </div>

                    <div class="pb-5">
                        <BaseSwitch v-model="popupItem.limit_per_item" color="primary" :label="$t('experience.step6.switch2')" />
                    </div>

                    <div class="pb-5">
                        <BaseSwitch v-model="popupItem.is_mandatory" color="primary" :label="$t('experience.step6.switch3')" />
                    </div>
                </div>
            </template>
            <template #actions>
                <BaseButton
                    type="button"
                    variant="primary"
                    customClass="!ppv2-min-h-[40px]"
                    class="mt-4"
                    @click="submitAdditionalSale"
                >
                    {{ $t('common.save') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import { Plus } from 'lucide-vue'
import AdditionalSaleCard from '../../../Shared/AdditionalSaleCard.vue'
import HeaderStep from './HeaderStep.vue'
import BackButton from '../../../Shared/BackButton.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BaseLabel from '../../../Shared/BaseLabel.vue'
import CustomButtonSave from '../../../Shared/CustomButtonSave.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import PreviewExperience from './PreviewExperience.vue'
import CustomDialog from '../../../Shared/CustomDialog.vue'
import CustomInputUpload from '../../../Inputs/CustomInputUpload.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import BaseTextarea from '../../../Shared/BaseTextarea.vue'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import { mapGetters, mapActions } from 'vuex'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
export default {
    components: {
        BaseTextarea,
        BaseButton,
        BackButton,
        HeaderStep,
        Plus,
        AdditionalSaleCard,
        CustomButtonSave,
        PreviewExperience,
        CustomDialog,
        CustomInputUpload,
        BaseLabel,
        CustomInput,
        BaseSwitch,
        BaseSelect,
    },
    props: {
        step: { type: Object, required: true },
        sizes: { type: Object, required: true },
        selected_experience: { type: Object, required: false },
        prestation: { type: Object, required: false },
        country_vat_rates: {
            type: Array,
            required: false,
        },
        products: {
            type: Array,
            required: false,
        },
        cats: {
            type: Array,
            required: false,
            default: () => [],
        },
        id: { type: [Number, String], default: null },
        category: {
            type: Object,
            required: false,
        },
        sub_category: {
            type: Object,
            required: false,
        },
        complited: {
            type: Boolean,
            required: false,
        },
        tenant: {
            type: Object,
            default: () => null,
        },
        planFormule: {
            type: Object,
            default: () => null,
        },
        planForfait: {
            type: Object,
            default: () => null,
        },
        planAbonnement: {
            type: Object,
            default: () => null,
        },
        planPublishAdditionalSales: {
            type: Object,
            default: () => null,
        },
        permissionCreateAdditionalSales: {
            type: Boolean,
            default: false,
        },
        permissionModifyAdditionalSales: {
            type: Boolean,
            default: false,
        },
        permissionDeleteAdditionalSales: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            popupVisible: false,
            popupTitle: this.$t('experience.step6.popup.title'),
            activeField: null,
            errors: {},
            option_tva: [],
            localPrestation: null,
            editIndex: null,
            popupItem: {
                id: null,
                name: '',
                price: '',
                description: '',
                image: '',
                tva: null,
                checked: true,
                file: '',
                deleted: false,
                participant_based_quantity: false,
                limit_per_item: false,
                is_mandatory: false,
                category_id: null,
                subcategory_id: null,
                category_selection: null,
            },
            loading: false,
            showExitPopup: false,
            initialDataString: null,
        }
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        getCardWidth() {
            const gap = this.normalizeSize(20, true)
            return `calc((100% - 4 * ${gap}) / 5)`
        },
        access_plan(user_plan_id, plan) {
            if (!plan) return false
            return user_plan_id >= plan.min_plan_level || plan.is_given_by_super_admin
        },
        toFixed2(v) {
            const n = Number(v)
            return Number.isFinite(n) ? n.toFixed(2) : '0.00'
        },
        // Frère de toFixed2 réservé aux TAUX de TVA. toFixed2 reste pour les montants,
        // qui se tiennent au centime ; un taux, lui, peut avoir 3 décimales (TVQ
        // québécoise à 9,975 %) et toFixed(2) l'écraserait en 9.98 avant l'envoi API.
        toRate(v) {
            const n = Number(v)
            return Number.isFinite(n) ? n : 0
        },
        getOriginalIndex(item) {
            return this.additional_sales.findIndex((sale) => sale === item)
        },
        normalizeSizeVertical,
        async saveAndExit() {
            this.$emit('save-and-exit')
        },
        async saveExperience(redirect = false) {
            this.loading = true
            this.error = null

            // Sanitize additional_sales (ensure no null descriptions, and no NaN price/tva)
            const cleanedSales = this.additional_sales.map((sale) => {
                const priceNum = Number(sale.price)
                const tvaNum = Number(sale.tva)
                return {
                    ...sale,
                    description: sale.description || '', // Ensure string
                    name: sale.name || '',
                    price: Number.isFinite(priceNum) ? priceNum : 0,
                    tva: Number.isFinite(tvaNum) ? tvaNum : 0,
                    category_id: sale.category_id || null,
                    subcategory_id: sale.subcategory_id || null,
                }
            })

            const formData = {
                step: this.step.id,
                additional_sales: cleanedSales,
                selectExperience: this.localSelectExperience,
                active: this.localPrestation?.active || false,
            }
            if (this.localPrestation?.id) formData.id = this.localPrestation.id

            try {
                const response = await axios.post('/api/v3/experience/save_exit_step_two', formData)
                const responseData = response.data
                const newPrestation = responseData.prestation || responseData.data

                // 1. Update global products list (parent state)
                if (responseData.new_products && responseData.new_products.length > 0) {
                    this.$emit('update-global-products', responseData.new_products)

                    // 2. Sync local additional_sales IDs that were missing
                    responseData.new_products.forEach((newProd) => {
                        const localSale = this.additional_sales.find((s) => !s.id && s.name === newProd.name)
                        if (localSale) {
                            localSale.id = newProd.id
                        }
                    })
                }

                this.$emit('update-prestation', newPrestation)

                if (redirect) {
                    const baseUrl = `${window.location.protocol}//${window.location.host}`
                    window.location.href = `${baseUrl}/back-office/v3/experience`
                }
                return true
            } catch (err) {
                console.error('❌ Erreur lors de la sauvegarde :', err)
                const errorMessage = err.response?.data?.message || err.message || this.$t('experience.errors.save')

                // Check for duplicate product error (structured 409 response)
                if (err.response?.status === 409 && err.response?.data?.error_code === 'PRODUCT_EXISTS') {
                    const productName = err.response.data.product_name
                    const customMessage = this.$t('experience.errors.productExists', { productName })

                    if (this.$toast) {
                        this.$toast.error(customMessage)
                    } else {
                        console.error('$toast plugin not available')
                    }
                } else {
                    // Fallback: show toast for other errors too
                    if (this.$toast) {
                        this.$toast.error(errorMessage)
                    } else {
                        console.error('$toast plugin not available')
                    }
                    this.error = errorMessage
                }
            } finally {
                this.loading = false
            }
            return false
        },
        async submitForm() {
            const success = await this.saveExperience(false)
            if (success) {
                this.$toast.success(this.$t('experience.saved'))
                if (!this.complited) {
                    this.$emit('next-step')
                }
            }
        },
        normalizeSize,
        getState(field) {
            if (this.errors[field]) return 'error'
            if (this.additional_sales[field]) return 'valid'
            return 'default'
        },
        setOptionTva() {
            if (Array.isArray(this.country_vat_rates)) {
                this.option_tva = this.country_vat_rates.map((tva) => ({
                    label: `${tva.pourcentage}%`,
                    // Valeur numérique brute : .toFixed(2) écrasait 9.975 en "9.98" et,
                    // au passage, empêchait la présélection (la chaîne "20.00" ne
                    // correspond pas au 20 renvoyé par l'API).
                    value: parseFloat(tva.pourcentage),
                }))
            }
        },
        setProucts() {
            const salesMap = {}
            const purchaseOptions = this.localPrestation?.product_purchase_options || []
            const serverCheckedIds = purchaseOptions.map((opt) => opt.item_id)
            const pivotByItemId = {}
            purchaseOptions.forEach((opt) => {
                pivotByItemId[opt.item_id] = opt
            })

            // Preserve deleted state from current store so archived products stay hidden after re-sync
            const deletedState = {}
            if (Array.isArray(this.additional_sales)) {
                this.additional_sales.forEach((sale) => {
                    if (sale.deleted && sale.id) {
                        deletedState[sale.id] = true
                    }
                })
            }

            // 1. Process products from the establishment list (prop)
            if (Array.isArray(this.products)) {
                this.products.forEach((product) => {
                    const pivot = pivotByItemId[product.id] || {}

                    // Determine category_selection
                    let category_selection = null
                    if (product.subcategory_id) {
                        category_selection = `sub_${product.subcategory_id}`
                    } else if (product.category_id) {
                        category_selection = `cat_${product.category_id}`
                    }

                    salesMap[product.id] = {
                        id: product.id,
                        name: product.name,
                        price: this.toFixed2(product.price),
                        tva: this.toRate(product.vat_rate),
                        image: product.img1 && product.img1.trim() !== '' ? product.img1 : '/media/bo/empty.jpg',
                        description: product.description,
                        checked: serverCheckedIds.includes(product.id),
                        deleted: deletedState[product.id] || false,
                        participant_based_quantity:
                            pivot.participant_based_quantity ?? product.participant_based_quantity ?? false,
                        limit_per_item: pivot.limit_per_item ?? product.limit_per_item ?? false,
                        is_mandatory: pivot.is_mandatory ?? product.is_mandatory ?? false,
                        category_id: product.category_id,
                        subcategory_id: product.subcategory_id,
                        category_selection: category_selection,
                    }
                })
            }

            // 2. Fallback for products NOT in the establishment list (e.g. newly created during this session)
            purchaseOptions.forEach((pivot) => {
                if (pivot.type === 'product' && pivot.item && !salesMap[pivot.item_id]) {
                    const product = pivot.item

                    // Determine category_selection
                    let category_selection = null
                    if (product.subcategory_id) {
                        category_selection = `sub_${product.subcategory_id}`
                    } else if (product.category_id) {
                        category_selection = `cat_${product.category_id}`
                    }

                    salesMap[product.id] = {
                        id: product.id,
                        name: product.name,
                        price: this.toFixed2(product.price),
                        tva: this.toRate(product.vat_rate),
                        image: product.img1 && product.img1.trim() !== '' ? product.img1 : '/media/bo/empty.jpg',
                        description: product.description,
                        checked: true,
                        deleted: deletedState[product.id] || false,
                        participant_based_quantity: pivot.participant_based_quantity ?? false,
                        limit_per_item: pivot.limit_per_item ?? false,
                        is_mandatory: pivot.is_mandatory ?? false,
                        category_id: product.category_id,
                        subcategory_id: product.subcategory_id,
                        category_selection: category_selection,
                    }
                }
            })

            this.setAdditionalSales(Object.values(salesMap))
        },
        submitAdditionalSale() {
            this.errors = {}

            // --- VALIDATION ---
            if (!this.popupItem.name) {
                this.errors.name = this.$t('experience.errors.required')
            }

            if (!this.popupItem.category_id && !this.popupItem.category_selection) {
                this.errors.category_id = this.$t('product.validation.category_required')
            }

            if (!this.popupItem.price) {
                this.errors.price = this.$t('experience.errors.required')
            } else if (isNaN(this.popupItem.price)) {
                this.errors.price = this.$t('experience.errors.invalidNumber')
            }

            if (this.popupItem.tva === null || this.popupItem.tva === '' || this.popupItem.tva === undefined) {
                this.errors.tva = this.$t('experience.errors.required')
            }

            if (Object.keys(this.errors).length > 0) return

            // --- FORMAT VALUES ---
            const isEdit = this.editIndex !== null
            const formattedItem = {
                id: this.popupItem.id ?? '',
                name: this.popupItem.name,
                price: this.toFixed2(this.popupItem.price),
                description: this.popupItem.description,
                image: this.popupItem.image || '/media/bo/empty.jpg',
                deleted: this.popupItem.deleted,
                tva: this.toRate(this.popupItem.tva),
                checked: isEdit ? this.popupItem.checked : this.access_plan(this.tenant.plan_id, this.planPublishAdditionalSales),
                file: this.popupItem.file || '',
                participant_based_quantity: this.popupItem.participant_based_quantity,
                limit_per_item: this.popupItem.limit_per_item,
                is_mandatory: this.popupItem.is_mandatory,
                category_id: this.popupItem.category_id,
                subcategory_id: this.popupItem.subcategory_id,
                category_selection: this.popupItem.category_selection,
            }

            // --- EDIT MODE ---
            if (isEdit) {
                this.updateAdditionalSale({ index: this.editIndex, sale: formattedItem })
            }
            // --- ADD MODE ---
            else {
                this.addAdditionalSale(formattedItem)
            }

            // --- RESET POPUP ---
            this.resetPopup()

            // Close popup
            this.popupVisible = false
        },

        clearError(field) {
            if (this.errors[field]) delete this.errors[field]
        },
        exit() {
            const baseUrl = `${window.location.protocol}//${window.location.host}`
            window.location.href = `${baseUrl}/back-office/v3/experience`
        },
        handleImageUpload(file) {
            if (file instanceof File) {
                this.popupItem.image = URL.createObjectURL(file)
            } else {
                this.popupItem.image = file // In case backend returns a ready URL
            }
        },
        updateAdditionalSaleLocal(updatedItem, index) {
            console.log(updatedItem)
            this.updateAdditionalSale({ index, sale: updatedItem })
        },
        openEditPopup(item, index) {
            this.editIndex = index
            this.popupItem = JSON.parse(JSON.stringify(item))
            this.popupVisible = true
        },
        deleteItem(index) {
            this.updateAdditionalSale({
                index,
                sale: {
                    ...this.additional_sales[index],
                    deleted: true,
                },
            })
        },
        openPopup() {
            this.editIndex = null
            this.resetPopup()
            this.popupVisible = true
        },
        resetPopup() {
            this.popupItem = {
                id: null,
                name: '',
                price: '',
                description: '',
                image: '',
                tva: null,
                checked: false,
                file: '',
                participant_based_quantity: false,
                limit_per_item: false,
                is_mandatory: false,
                category_id: null,
                subcategory_id: null,
                category_selection: null,
            }
            this.editIndex = null
        },
        onFileSelected(file) {
            if (!file) return
            const reader = new FileReader()
            reader.onload = () => {
                this.popupItem.file = reader.result // Base64 string
                this.popupItem.image = reader.result // Base64 string
            }
            reader.readAsDataURL(file)
        },
        getUserChoiceValue(key) {
            if (!this.localPrestation || !this.localPrestation.user_choices) return null
            const choice = this.localPrestation.user_choices.find((c) => c.key === key)
            return choice ? choice.value : null
        },
        ...mapActions('experience', [
            'setAdditionalSales',
            'addAdditionalSale',
            'updateAdditionalSale',
            'setDisplayFacilitiesNames',
        ]),
        getFormDataString() {
            return JSON.stringify({
                additional_sales: this.additional_sales,
                display_facilities_names: this.display_facilities_names,
            })
        },
        hasChanges() {
            if (!this.initialDataString) return false
            return this.initialDataString !== this.getFormDataString()
        },
    },
    computed: {
        ...mapGetters('experience', ['additional_sales']),
        categoryOptions() {
            const options = []
            this.cats.forEach((cat) => {
                options.push({ label: cat.name, value: `cat_${cat.id}` })
                if (cat.children && cat.children.length > 0) {
                    cat.children.forEach((sub) => {
                        options.push({ label: `- ${sub.name}`, value: `sub_${sub.id}` })
                    })
                } else if (cat.subcategories && cat.subcategories.length > 0) {
                    cat.subcategories.forEach((sub) => {
                        options.push({ label: `- ${sub.name}`, value: `sub_${sub.id}` })
                    })
                }
            })
            return options
        },
        filteredAdditionalSales() {
            return this.additional_sales.filter((sale) => !sale.deleted)
        },
        hasAccess() {
            return Boolean(this.access_plan(this.tenant?.plan_id, this.planPublishAdditionalSales))
        },
        display_facilities_names: {
            get() {
                return this.$store.getters['experience/display_facilities_names']
            },
            set(value) {
                this.setDisplayFacilitiesNames(value)
            },
        },
        localSelectExperience() {
            return this.selected_experience || {}
        },
        hasId() {
            return this.id !== null && this.id !== undefined
        },
    },
    mounted() {
        this.setOptionTva()
        this.setProucts()
    },
    watch: {
        'popupItem.category_selection'(newVal) {
            if (newVal) {
                if (newVal.startsWith('sub_')) {
                    const subId = parseInt(newVal.replace('sub_', ''))
                    this.popupItem.subcategory_id = subId
                    // Find parent category
                    const parentCat = this.cats.find(
                        (c) =>
                            (c.subcategories && c.subcategories.some((s) => s.id === subId)) ||
                            (c.children && c.children.some((s) => s.id === subId))
                    )
                    if (parentCat) {
                        this.popupItem.category_id = parentCat.id
                    }
                } else if (newVal.startsWith('cat_')) {
                    this.popupItem.category_id = parseInt(newVal.replace('cat_', ''))
                    this.popupItem.subcategory_id = null
                }
                this.clearError('category_id')
            } else {
                this.popupItem.category_id = null
                this.popupItem.subcategory_id = null
                this.clearError('category_id')
            }
        },
        country_vat_rates: {
            handler() {
                this.setOptionTva()
            },
            deep: true,
            immediate: true,
        },
        products: {
            handler() {
                this.setProucts()
            },
            deep: true,
            immediate: true,
        },
        products: {
            handler(newVal) {
                if (newVal) {
                    this.setProucts()
                }
            },
            deep: true,
        },
        prestation: {
            handler(newVal) {
                if (newVal) {
                    this.localPrestation = newVal
                    // ✅ Re-sync product switch values from pivot after save
                    this.setProucts()

                    const displayNames = this.getUserChoiceValue('client_space_choice')
                    if (displayNames !== null) {
                        this.setDisplayFacilitiesNames(
                            displayNames == 1 || displayNames == '1' || displayNames == true || displayNames == 'true'
                        )
                    }
                    this.$nextTick(() => {
                        this.initialDataString = this.getFormDataString()
                    })
                }
            },
            immediate: true,
            deep: true,
        },
        additional_sales: {
            handler() {
                if (!this.initialDataString && this.additional_sales && this.additional_sales.length > 0) {
                    this.initialDataString = this.getFormDataString()
                }
            },
            deep: true,
        },
    },
}
</script>
<style scoped>
.my-block {
    width: 100%;
    height: 100%;
    border-radius: 15px;
    box-shadow: 0 2px 14px 0 rgba(0, 0, 0, 0.25);
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.upload-container {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 20px;
    opacity: 0.7;
}

.upload-container:hover,
.upload-container:focus,
.upload-container:active {
    opacity: 1;
}

.form-section {
    flex: 1;
}
</style>
